// src/components/EnhancedPracticeLobby/EnhancedPracticeLobby.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import './EnhancedPracticeLobby.css';

const EnhancedPracticeLobby = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { joinPractice } = useWebSocket();
  const [activeTab, setActiveTab] = useState('computer'); // 'online' or 'computer'
  const [startingPractice, setStartingPractice] = useState(false);

  const practiceModes = [
    {
      id: 'base',
      name: 'Base Practice',
      description: 'Play a full rummy game against 3 AI opponents. Use Auto-Arrange and Hint whenever you are stuck.',
      difficulty: 'Practice',
      aiLevel: 1,
      color: '#4CAF50',
      players: '1 Player + 3 AI',
      duration: 'Unlimited'
    }
  ];

  const onlineModes = [
    {
      id: 'quick-match',
      name: 'Quick Match',
      description: 'Play against random online opponents',
      players: '2-6 Players',
      duration: '15-25 mins',
      status: 'Coming Soon',
      color: '#2196F3'
    },
    {
      id: 'tournament',
      name: 'Tournament',
      description: 'Join competitive tournaments with prizes',
      players: '50+ Players',
      duration: '30-60 mins',
      status: 'Coming Soon',
      color: '#9C27B0'
    },
    {
      id: 'private-table',
      name: 'Private Table',
      description: 'Create private games with friends',
      players: '2-6 Players',
      duration: '15-25 mins',
      status: 'Coming Soon',
      color: '#607D8B'
    }
  ];

  const handlePracticeModeClick = async () => {
    if (startingPractice) return;
    setStartingPractice(true);
    try {
      const result = await joinPractice({ aiCount: 3, gameMode: 'points' });
      if (result && result.gameId) {
        navigate(`/game/${result.gameId}`);
      } else {
        alert(result?.error || 'Failed to start practice game. Please try again.');
      }
    } catch (err) {
      console.error('Practice join error:', err);
      alert(err?.message || 'Failed to start practice game');
    } finally {
      setStartingPractice(false);
    }
  };

  const handleOnlineModeClick = (mode) => {
    // For now, show coming soon message
    alert(`${mode.name} is coming soon!`);
  };

  return (
    <div className="enhanced-practice-lobby">
      <div className="lobby-header">
        <h1 className="lobby-title">User Vs. AI Practice Lobby</h1>
        <p className="lobby-subtitle">Welcome, {currentUser?.username || currentUser?.email}! Choose your game mode.</p>
      </div>

      <div className="lobby-content">
        {/* Game Mode Tabs */}
        <div className="game-mode-tabs">
          <button 
            className={`tab-button ${activeTab === 'online' ? 'active' : ''}`}
            onClick={() => setActiveTab('online')}
          >
            Play with Online Users
          </button>
          <button 
            className={`tab-button ${activeTab === 'computer' ? 'active' : ''}`}
            onClick={() => setActiveTab('computer')}
          >
            Play with Computer
          </button>
        </div>

        {/* Online Users Section */}
        {activeTab === 'online' && (
          <div className="online-section">
            <div className="section-header">
              <h2>Online Multiplayer</h2>
              <p>Challenge real players from around the world</p>
            </div>
            
            <div className="modes-grid">
              {onlineModes.map(mode => (
                <div
                  key={mode.id}
                  className="mode-card online-card"
                  style={{ borderColor: mode.color }}
                  onClick={() => handleOnlineModeClick(mode)}
                >
                  <div className="mode-header" style={{ backgroundColor: mode.color }}>
                    <h3>{mode.name}</h3>
                    <span className="status-badge">{mode.status}</span>
                  </div>
                  <div className="mode-body">
                    <p>{mode.description}</p>
                    <div className="mode-details">
                      <div className="detail-item">
                        <span className="detail-label">Players:</span>
                        <span className="detail-value">{mode.players}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Duration:</span>
                        <span className="detail-value">{mode.duration}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mode-footer">
                    <button className="play-button coming-soon">
                      Coming Soon
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Computer Practice Section */}
        {activeTab === 'computer' && (
          <div className="computer-section">
            <div className="section-header">
              <h2>Practice with AI</h2>
              <p>Improve your skills against computer opponents</p>
            </div>
            
            <div className="modes-grid">
              {practiceModes.map(mode => (
                <div
                  key={mode.id}
                  className="mode-card practice-card"
                  style={{ borderColor: mode.color }}
                  onClick={() => handlePracticeModeClick(mode)}
                >
                  <div className="mode-header" style={{ backgroundColor: mode.color }}>
                    <h3>{mode.name}</h3>
                    <span className="difficulty-badge">{mode.difficulty}</span>
                  </div>
                  <div className="mode-body">
                    <p>{mode.description}</p>
                    <div className="mode-details">
                      <div className="detail-item">
                        <span className="detail-label">AI Level:</span>
                        <span className="detail-value">{mode.aiLevel}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Players:</span>
                        <span className="detail-value">{mode.players}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Duration:</span>
                        <span className="detail-value">{mode.duration}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mode-footer">
                    <button className="play-button" style={{ backgroundColor: mode.color }}>
                      {startingPractice ? 'Starting...' : 'Play Now'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Stats */}
            <div className="quick-stats">
              <h3>Practice Benefits</h3>
              <div className="benefits-grid">
                <div className="benefit-item">
                  <div className="benefit-icon">🎯</div>
                  <div className="benefit-content">
                    <h4>Learn Strategies</h4>
                    <p>Master different playing techniques</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">⚡</div>
                  <div className="benefit-content">
                    <h4>No Pressure</h4>
                    <p>Play at your own pace</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">📈</div>
                  <div className="benefit-content">
                    <h4>Track Progress</h4>
                    <p>Monitor your improvement</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🎮</div>
                  <div className="benefit-content">
                    <h4>Unlimited Games</h4>
                    <p>Play as much as you want</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedPracticeLobby;